/**
 * Clima a partir da API pública Open-Meteo (sem chave).
 * Geocoding: https://open-meteo.com/en/docs/geocoding-api
 * Arquivo histórico: https://open-meteo.com/en/docs/historical-weather-api
 */

const MONTHS_LONG = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

function normalizeCountryHint(hint) {
  if (!hint || typeof hint !== "string") return "";
  const t = hint.trim().toLowerCase();
  if (t.length === 2) return t;
  return t;
}

/**
 * @param {string} cityName
 * @param {string} [countryHint] — nome do país ou código ISO2
 */
export async function geocodeCity(cityName, countryHint) {
  const name = String(cityName || "").trim();
  if (!name) throw new Error("Nome da cidade vazio");

  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=10&language=pt`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Falha ao buscar coordenadas");
  const data = await res.json();
  if (!data.results?.length) throw new Error(`Local não encontrado: ${name}`);

  const hint = normalizeCountryHint(countryHint);
  let best = data.results[0];
  if (hint) {
    const match = data.results.find((r) => {
      const c = (r.country || "").toLowerCase();
      const code = (r.country_code || "").toLowerCase();
      if (hint.length === 2) return code === hint;
      return c.includes(hint) || hint.includes(c.slice(0, 4));
    });
    if (match) best = match;
  }

  return {
    latitude: best.latitude,
    longitude: best.longitude,
    displayName: best.name,
    country: best.country,
  };
}

function aggregateMonthlyMaxFromDaily(times, temps) {
  const buckets = Array.from({ length: 12 }, () => []);
  for (let i = 0; i < times.length; i++) {
    const t = times[i];
    const v = temps[i];
    if (v == null || Number.isNaN(Number(v)) || typeof t !== "string") continue;
    const month = parseInt(t.slice(5, 7), 10) - 1;
    if (month < 0 || month > 11) continue;
    buckets[month].push(Number(v));
  }
  return buckets.map((arr) => {
    if (!arr.length) return 0;
    return Math.round(Math.max(...arr) * 10) / 10;
  });
}

function buildBestTimeLabel(monthlyAvgs, year) {
  const peak = monthlyAvgs.indexOf(Math.max(...monthlyAvgs));
  const warmIdx = monthlyAvgs
    .map((t, i) => ({ t, i }))
    .filter((x) => x.t >= 20)
    .map((x) => x.i);
  if (!warmIdx.length) {
    return `${year}: pico em ${MONTHS_LONG[peak]} (~${Math.round(monthlyAvgs[peak])}°C máx. média)`;
  }
  const from = Math.min(...warmIdx);
  const to = Math.max(...warmIdx);
  if (from === to) {
    return `${MONTHS_LONG[from]} (${year}, ~${Math.round(monthlyAvgs[from])}°C máx. média)`;
  }
  return `${MONTHS_LONG[from]} a ${MONTHS_LONG[to]} (${year}, máximas médias mais altas)`;
}

/**
 * @returns {Promise<{ monthlyHighs: number[], bestTimeLabel: string, highlightMonth: number, tooltipTemp: string, sourceYear: number }>}
 */
export async function fetchClimateProfile(cityName, countryHint) {
  const { latitude, longitude } = await geocodeCity(cityName, countryHint);
  const year = new Date().getFullYear() - 1;
  const url =
    `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}` +
    `&start_date=${year}-01-01&end_date=${year}-12-31&daily=temperature_2m_max&timezone=auto`;

  const res = await fetch(url);
  if (!res.ok) throw new Error("Falha ao buscar histórico de temperatura");
  const data = await res.json();
  const times = data.daily?.time;
  const temps = data.daily?.temperature_2m_max;
  if (!Array.isArray(times) || !Array.isArray(temps) || !times.length) {
    throw new Error("Resposta de clima incompleta");
  }

  const monthlyHighs = aggregateMonthlyMaxFromDaily(times, temps);
  const highlightMonth = monthlyHighs.indexOf(Math.max(...monthlyHighs));
  const peakVal = monthlyHighs[highlightMonth];
  const bestTimeLabel = buildBestTimeLabel(monthlyHighs, year);
  const tooltipTemp = `${Math.round(peakVal)}°`;

  return {
    monthlyHighs,
    bestTimeLabel,
    highlightMonth,
    tooltipTemp,
    sourceYear: year,
  };
}
